import { useTranslation } from "react-i18next";
import type {
  FinalLeaderboardEntry,
  LeaderboardEntry,
  LeaderboardEntryWithDelta,
} from "@binogi-livequiz/shared";

type Entry = LeaderboardEntry | LeaderboardEntryWithDelta | FinalLeaderboardEntry;

function hasDelta(e: Entry): e is LeaderboardEntryWithDelta {
  return (
    "delta" in e &&
    typeof (e as LeaderboardEntryWithDelta).delta === "number"
  );
}

function hasRank(e: Entry): e is FinalLeaderboardEntry {
  return "rank" in e && typeof (e as FinalLeaderboardEntry).rank === "number";
}

interface LeaderboardProps {
  entries: Entry[];
  highlight?: string;
  compact?: boolean;
}

export function Leaderboard({ entries, highlight, compact }: LeaderboardProps) {
  const { t } = useTranslation();
  if (entries.length === 0) {
    return (
      <div className="text-white/60 text-sm italic">{t("common.no_scores")}</div>
    );
  }
  return (
    <ol className="space-y-2 w-full">
      {entries.map((e, i) => {
        const rank = hasRank(e) ? e.rank : i + 1;
        const isMe = highlight && e.displayName === highlight;
        return (
          <li
            key={`${e.displayName}-${i}`}
            className={`flex items-center justify-between rounded-xl px-3 py-2 ${compact ? "text-sm" : ""} ${
              isMe
                ? "bg-brand-purple/30 ring-2 ring-brand-purple"
                : "bg-brand-ink2"
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <span
                className={`rounded-full bg-brand-ink3 inline-flex items-center justify-center font-bold flex-shrink-0 ${compact ? "w-6 h-6 text-xs" : "w-7 h-7 text-sm"}`}
              >
                {rank}
              </span>
              <span className="font-semibold truncate">{e.displayName}</span>
              {hasDelta(e) && e.delta > 0 && (
                <span className="text-xs text-brand-good flex-shrink-0">
                  +{e.delta}
                </span>
              )}
            </div>
            <span className="font-bold tabular-nums flex-shrink-0">
              {e.score}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
